import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'

/**
 * **Navigation an der Überschrift einer Position** (AP-35 Nachtrag 15, AP-37 Nachtrag 8) — gemeinsam für
 * Projektmanagement und Kalkulation, seit die Positionsliste links entfallen ist:
 *
 * - `Blaettern`: ‹ › und der Zähler „n/gesamt" links vor der Bezeichnung;
 * - `WahlFenster`: die Aufklappliste unter der Überschrift — oben das Suchfeld (hat sofort den Cursor),
 *   darunter, was die Maske hineinlegt. Einträge sind Knöpfe mit `data-wahl`; ↑ ↓ wählt, Enter öffnet,
 *   Esc und ein Klick daneben schließen;
 * - `useWahlTasten`: Alt + ← / → blättert, Alt + ↓ öffnet die Liste — von überall auf der Seite.
 *
 * Was in der Liste steht (Zustand und EP in der Kalkulation, Leistungsstand und Markieren im Projekt), weiß
 * nur die Maske; der Baustein kennt keine Position.
 */

const knopf =
  'inline-flex h-[26px] w-[26px] items-center justify-center border border-border bg-background text-foreground hover:bg-muted disabled:cursor-default disabled:opacity-40 disabled:hover:bg-background'

/** ‹ › und „n/gesamt". Am Ende geht es zum Anfang weiter — das entscheidet der Aufrufer in `onSchritt`. */
export function Blaettern({
  stelle,
  anzahl,
  onSchritt,
  titelZurueck,
  titelVor,
}: {
  /** Stelle der geöffneten Position in der Reihenfolge, ab 0; −1, wenn sie nicht darin steht. */
  stelle: number
  anzahl: number
  onSchritt: (schritt: 1 | -1) => void
  titelZurueck?: string
  titelVor?: string
}) {
  const allein = anzahl < 2
  return (
    <div className="flex shrink-0 items-center gap-2">
      <span className="inline-flex">
        <button type="button" className={`${knopf} rounded-l-md`} disabled={allein} onClick={() => onSchritt(-1)} title={titelZurueck ?? 'Vorige Position (Alt + ←)'} aria-label="Vorige Position">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button type="button" className={`${knopf} -ml-px rounded-r-md`} disabled={allein} onClick={() => onSchritt(1)} title={titelVor ?? 'Nächste Position (Alt + →)'} aria-label="Nächste Position">
          <ChevronRight className="h-4 w-4" />
        </button>
      </span>
      {anzahl > 0 && (
        <span className="min-w-[1.75rem] text-xs tabular-nums text-muted-foreground" title="Stelle in der Positionsliste">
          {stelle >= 0 ? stelle + 1 : '–'}/{anzahl}
        </span>
      )}
    </div>
  )
}

/** Klasse eines Eintrags der Liste — hervorgehoben, während ↑ ↓ auf ihm steht (`data-hl`). */
export const WAHL_EINTRAG = 'data-[hl=true]:bg-primary/15'

/**
 * Die Aufklappliste. Sie sitzt in einem `relative`-Element an der Überschrift und öffnet darunter nach rechts.
 * `children` ist die Liste selbst; ihre wählbaren Einträge tragen `data-wahl` (und `data-aktuell` an der
 * geöffneten Position — dort beginnt die Tastaturwahl).
 */
export function WahlFenster({
  suche,
  onSuche,
  onSchliessen,
  kopf,
  nebenSuche,
  fuss,
  bezeichnung = 'Positionen',
  children,
}: {
  suche: string
  onSuche: (text: string) => void
  onSchliessen: () => void
  /** Über dem Suchfeld, z. B. „+ Position". */
  kopf?: ReactNode
  /** Rechts neben dem Suchfeld, z. B. ein Schalter. */
  nebenSuche?: ReactNode
  fuss?: ReactNode
  bezeichnung?: string
  children: ReactNode
}) {
  const rahmen = useRef<HTMLDivElement>(null)
  const liste = useRef<HTMLDivElement>(null)
  const eingabe = useRef<HTMLInputElement>(null)
  const [hl, setHl] = useState(-1)
  const schliessen = useRef(onSchliessen)
  schliessen.current = onSchliessen

  const eintraege = () => [...(liste.current?.querySelectorAll<HTMLElement>('[data-wahl]') ?? [])]

  useEffect(() => {
    eingabe.current?.focus()
    // Die geöffnete Position ist sichtbar und Ausgangspunkt der Tastaturwahl — auch bei 300 Positionen.
    const alle = eintraege()
    const i = alle.findIndex((e) => e.hasAttribute('data-aktuell'))
    if (i >= 0) {
      alle[i].scrollIntoView?.({ block: 'nearest' })
      setHl(i)
    }
    const weg = (e: MouseEvent) => {
      if (rahmen.current && !rahmen.current.contains(e.target as Node)) schliessen.current()
    }
    document.addEventListener('mousedown', weg)
    return () => document.removeEventListener('mousedown', weg)
  }, [])

  // Die Hervorhebung steht am Element, nicht im Zustand der Maske: Die Liste gehört ihr, die Tasten dem Fenster.
  useEffect(() => {
    eintraege().forEach((e, i) => {
      if (i === hl) {
        e.setAttribute('data-hl', 'true')
        e.scrollIntoView?.({ block: 'nearest' })
      } else e.removeAttribute('data-hl')
    })
  })

  return (
    <div
      ref={rahmen}
      role="dialog"
      aria-label={bezeichnung}
      className="absolute left-0 top-[calc(100%+0.375rem)] z-50 w-[28.75rem] max-w-[calc(100vw-2rem)] rounded-md border border-border p-2 text-left text-sm font-normal shadow-lg"
      style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
      onKeyDown={(e) => {
        // Esc schließt nur das Fenster — es läuft nicht weiter zu einer Zeile oder Maske darunter.
        if (e.key === 'Escape') {
          e.stopPropagation()
          onSchliessen()
        }
      }}
    >
      {kopf && <div className="mb-2 flex items-center gap-1">{kopf}</div>}
      <div className="mb-2 flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={eingabe}
            type="text"
            value={suche}
            onChange={(e) => {
              onSuche(e.target.value)
              setHl(0)
            }}
            onKeyDown={(e) => {
              const n = eintraege().length
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setHl((h) => Math.min(h + 1, n - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setHl((h) => Math.max(h - 1, 0))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                eintraege()[hl]?.click()
              }
            }}
            placeholder="Suchen …"
            aria-label="Position suchen"
            autoComplete="off"
            className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        {nebenSuche}
      </div>
      <div ref={liste} className="max-h-[60vh] space-y-3 overflow-y-auto">
        {children}
      </div>
      {fuss && <div className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">{fuss}</div>}
    </div>
  )
}

/**
 * Alt + ← / → blättert, Alt + ↓ öffnet die Liste. Seitenweit, aber nur mit Alt — die Pfeile allein gehören den
 * Feldern, und `/`, `[`, `]` dem Rahmen des Projekts darüber.
 */
export function useWahlTasten({ aktiv, onSchritt, onOeffnen }: { aktiv: boolean; onSchritt: (schritt: 1 | -1) => void; onOeffnen: () => void }) {
  const schritt = useRef(onSchritt)
  const oeffnen = useRef(onOeffnen)
  schritt.current = onSchritt
  oeffnen.current = onOeffnen
  useEffect(() => {
    if (!aktiv) return
    const taste = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      // In einer Auswahl öffnet Alt + ↓ deren eigene Liste — die bleibt ihr.
      if (e.key === 'ArrowDown' && (e.target as HTMLElement | null)?.tagName === 'SELECT') return
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        schritt.current(e.key === 'ArrowRight' ? 1 : -1)
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        oeffnen.current()
      }
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [aktiv])
}

/** Was der Kopf einer Position zum Blättern und Aufklappen braucht — die Maske baut es, der Kopf zeichnet es. */
export interface PositionsNavigation {
  stelle: number
  anzahl: number
  schritt: (schritt: 1 | -1) => void
  offen: boolean
  setOffen: (offen: boolean) => void
  /** Das `WahlFenster` samt Inhalt — nur gezeichnet, solange `offen`. */
  fenster: ReactNode
}
